import { AnyType } from '@src/models/common';
import { getCookieString, ICookieOptions } from '@src/utils/cookies';
import { GetServerSidePropsContext, GetServerSidePropsResult, IAuthRequestParams, TDefaultAsyncStore } from './models';
import { isAuthServerRequests } from './utils';
import { ACCESS_TOKEN_MAX_AGE_MINUTE, DEFAULT_COOKIE_OPTIONS, REFRESH_TOKEN_MAX_AGE_MINUTE } from '@src/consts/cookies';
import { AuthClient } from '../authClient';
import { getUserInfo } from '@src/utils/ssr';
import { withTags } from '@src/utils/tags';
import { NgApiClient } from '../ngApiClient';

/**
 * <---------ТОЛЬКО ДЛЯ СРЕДЫ `NODE`----------->
 *
 * Больше чем функция обертка для getServeSideProps
 * Во-первых функция берет всю работу с авторизацией на себя, если переданы соответствующие параметры
 * Во-вторых функция оборачивает вложенный в нее callback `AsyncLocalStorage run`,
 * т.е. вам будет доступен везде внутри колбека `store` запакованный в `requestStore` (аналог useContext).
 *
 * Обрабатывает только 401 ошибку!
 * Если не удается авторизоваться по refreshToken`у, удаляет все токены из кук, и выполняет колбек,
 * предполагая, что на клиенте будет доп. проверка и соответственно редирект
 *
 * !!!!!!!TODO: возможно стоит доделать и делать редирект на логин с сервака!!!!!
 *
 * Основные параметры.
 * - `requestStore` - инстанс AsyncLocalStorage https://nodejs.org/api/async_context.html#class-asynclocalstorage.
 * - `envConfig` - переменные `env` полученные с помощью функции `getNextEnvConfig`.
 * - `allowedTags` - список разрешенных тегов для текущей страницы.
 * - `isAuthZone` - можно явно задать обработку авторизации
 */
export const serverRequest = <PropsType = AnyType>(
  { requestStore, envConfig, allowedTags, isAuthZone: paramsIsAuthZone }: IAuthRequestParams,
  cb: (context: GetServerSidePropsContext) => Promise<GetServerSidePropsResult<PropsType>>,
) => {
  return async (context: GetServerSidePropsContext) => {
    const { cookieName, authZoneHeaderName, authUrl } = envConfig;
    const refreshTokenName = `${cookieName}_r`;

    const isAuthZone =
      paramsIsAuthZone !== undefined
        ? paramsIsAuthZone
        : !!authZoneHeaderName && isAuthServerRequests({ authZoneHeaderName, context });

    const ngApiClient = new NgApiClient(envConfig);

    ngApiClient.restClient.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error?.response?.status !== 401) return Promise.reject(error);

        const options: ICookieOptions = DEFAULT_COOKIE_OPTIONS;
        if (context.req.headers.host?.includes('localhost')) {
          options.domain = false;
        }

        context.res.setHeader(
          'Set-Cookie',
          getCookieString(cookieName, 'false', {
            ...options,
            'max-age': -1,
          }),
        );

        return Promise.reject(error);
      },
    );

    ngApiClient.setIsAuthRequest(isAuthZone);
    if (isAuthZone) {
      ngApiClient.setServerCookieCallback(() => context);
    }

    const store: TDefaultAsyncStore = {
      ngApiClient,
      isAuthZone,
    };

    return await requestStore.run(store, async () => {
      try {
        if (!isAuthZone) {
          return await cb(context);
        }

        const accessToken = context.req.cookies[cookieName];

        if (accessToken) {
          if (typeof allowedTags === 'string' || (Array.isArray(allowedTags) && allowedTags.length > 0)) {
            const { user } = getUserInfo(context as AnyType, cookieName);
            const isAllowed = user && withTags({ user, tags: allowedTags });

            if (!isAllowed) {
              return Promise.resolve({
                notFound: true,
              });
            }
          }

          return await cb(context);
        }

        const refreshToken = context.req.cookies[refreshTokenName];
        const options: ICookieOptions = DEFAULT_COOKIE_OPTIONS;
        const deleteRefreshToken = () => {
          // Удаляем refreshToken, пусть дальше AppProvider выполнить редирект на логин и обратно
          context.res.setHeader(
            'Set-Cookie',
            getCookieString(refreshTokenName, 'false', {
              ...options,
              'max-age': -1,
            }),
          );
        };

        if (refreshToken) {
          if (context.req.headers.host?.includes('localhost')) {
            options.domain = false;
          }

          try {
            const authClient = new AuthClient({
              baseUrl: authUrl,
            });

            const data = await authClient.authorizing(refreshToken);

            if (data) {
              context.req.cookies[cookieName] = String(data.accessToken);
              context.req.cookies[refreshTokenName] = String(data.refreshToken);

              context.res.setHeader(
                'Set-Cookie',
                getCookieString(cookieName, data.accessToken, {
                  ...options,
                  'max-age': ACCESS_TOKEN_MAX_AGE_MINUTE * 60,
                }),
              );
              context.res.setHeader(
                'Set-Cookie',
                getCookieString(refreshTokenName, data.refreshToken, {
                  ...options,
                  'max-age': REFRESH_TOKEN_MAX_AGE_MINUTE * 60,
                }),
              );
            }
          } catch (error) {
            deleteRefreshToken();
          }
        } else {
          deleteRefreshToken();
        }

        return await cb(context);
      } catch (error: AnyType) {
        console.error(error);
        if (error?.response?.status === 401) {
          return Promise.resolve({
            redirect: {
              destination: context.resolvedUrl,
              permanent: false,
            },
          });
        }

        return await cb(context);
      }
    });
  };
};
